/**
 * BAP Service - Client-Side Identity Management
 *
 * Provides access to BAP identities from decrypted backup stored in sessionStorage.
 * Handles both master keys (can create multiple identities) and member keys (single identity).
 */

import type { BapMasterBackup } from "bitcoin-backup";
import { isLegacyBackup, isType42Backup } from "bitcoin-backup";
import { BAP, type MasterID } from "bsv-bap";
import type { BAPProfile } from "@/types/bap";
import { normalizeImageUrl } from "../image-utils";
import { STORAGE_KEYS } from "../storage-keys";
import { syncBackupToStorage, updateBapIdsInBackup } from "./backup";
import {
	decryptIdentityBackup,
	type IdentityBackup,
	isSeedIdentityBackup,
	validateIdentityBackup,
} from "./identity-backup";
import {
	SeedProfileAdapter,
	type SeedProfileIdentity,
} from "./seed-profile-adapter";
import { getAccountPubkey } from "./utils";

export interface ResolvedIdentity {
	bapId: string; // The identity key (BAP ID)
	name: string; // Local name from backup
	displayName?: string; // Resolved display name from on-chain profile
	rootAddress: string;
	counter: number;
	accountPubkey?: string; // Stable account public key used for identity linkage
	// Full BAP profile data (from cache or on-chain)
	profile?: BAPProfile | null;
}

// Re-export for backwards compatibility
export type { BAPIdentityData, BAPProfile } from "@/types/bap";

interface BAPResponse {
	status: string;
	result: BAPProfile;
	isDraft?: boolean;
}

/**
 * Get decrypted backup from sessionStorage
 */
export function getDecryptedBackup(): IdentityBackup | null {
	const backupStr = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
	if (!backupStr) {
		return null;
	}

	try {
		return validateIdentityBackup(JSON.parse(backupStr));
	} catch (_error) {
		return null;
	}
}

/**
 * Save updated backup to both sessionStorage (decrypted) and localStorage (encrypted)
 * This ensures the backup persists across browser sessions
 * @deprecated Use syncBackupToStorage from backup-sync.ts instead for atomic updates
 */
export async function saveUpdatedBackup(
	updatedBackup: IdentityBackup,
	password?: string
): Promise<void> {
	// Use the atomic backup sync utility
	await syncBackupToStorage(updatedBackup, password);
}

/**
 * Extract account public key from a MasterID instance.
 */
function extractAccountPubkeyFromMaster(master: MasterID): string | undefined {
	try {
		return getAccountPubkey(master);
	} catch {
		return undefined;
	}
}

/**
 * Initialize BAP from backup and get all available identities
 */
export function getAvailableIdentities(): ResolvedIdentity[] {
	const backup = getDecryptedBackup();
	if (!backup) {
		return [];
	}

	if (isSeedIdentityBackup(backup)) {
		const adapter = new SeedProfileAdapter(backup);
		return backup.profiles.map((profile) => {
			const identity = adapter.getId(profile.bapId);
			if (!identity) {
				throw new Error("Invalid seed profile inventory");
			}
			return {
				bapId: profile.bapId,
				name:
					typeof profile.metadata?.name === "string"
						? profile.metadata.name
						: `Identity ${profile.index + 1}`,
				rootAddress: identity.rootAddress,
				counter: profile.index,
				accountPubkey: getAccountPubkey(identity),
			};
		});
	}

	// Handle BAP master backup (can have multiple identities)
	// Use centralized BAP instance getter
	const bap = getBAPInstance();
	if (!bap) {
		return [];
	}

	// Get all identity keys
	const idKeys = bap.listIds();

	// Map to our identity format
	const identities: ResolvedIdentity[] = [];

	for (const bapId of idKeys) {
		const master = bap.getId(bapId);
		if (master) {
			const accountPubkey = extractAccountPubkeyFromMaster(master);

			const identity: ResolvedIdentity = {
				bapId,
				name: `Identity ${identities.length + 1}`,
				rootAddress: master.rootAddress,
				counter: identities.length,
				accountPubkey,
				profile: undefined,
			};
			identities.push(identity);
		}
	}

	return identities;
}

export type SyncProgressCallback = (
	step: string,
	status: "in_progress" | "completed" | "error",
	error?: string
) => void;

/**
 * Sync identities from blockchain (like sigmaidentity's syncIdentitiesFromChain)
 * 1. Fetches profiles for existing IDs in backup
 * 2. Discovers new identities with newId() until 404
 * 3. Updates backup with discovered IDs
 * Returns count of newly discovered identities
 */
export async function syncIdentitiesFromChain(
	onProgress?: SyncProgressCallback
): Promise<number> {
	const bap = getBAPInstance();
	if (!bap) {
		onProgress?.(
			"Failed to initialize BAP",
			"error",
			"Failed to get BAP instance"
		);
		return 0;
	}

	const backup = getDecryptedBackup();
	if (!backup) {
		return 0;
	}

	// 1. Get existing IDs from backup
	const existingIds = bap.listIds();

	// 2. Fetch profiles for existing IDs
	onProgress?.("Checking existing identities on blockchain", "in_progress");
	await Promise.all(
		existingIds.map(async (bapId) => {
			try {
				return await fetchBAPProfile(bapId);
			} catch {
				return null;
			}
		})
	);

	// 3. Discover new identities
	onProgress?.("Scanning blockchain for new identities", "in_progress");
	const newProfiles: BAPProfile[] = [];
	let discoverCounter = 0;

	while (discoverCounter < 20) {
		try {
			// Create the next identity (this adds it to the BAP instance)
			const nextId = bap.newId();
			const nextIdKey = nextId.bapId;

			const profile = await fetchBAPProfile(nextIdKey).catch(() => null);

			if (profile) {
				newProfiles.push(profile);
				onProgress?.(
					`Found ${newProfiles.length} new on-chain identities`,
					"in_progress"
				);
				discoverCounter++;
			} else {
				// Remove the identity we just added since it has no on-chain profile
				bap.removeId(nextIdKey);
				break;
			}
		} catch {
			break;
		}
	}

	// 4. Update backup with discovered identities using atomic sync
	const finalIds = bap.exportIds();
	const masterBackup = backup as BapMasterBackup;

	if (newProfiles.length > 0) {
		const updatedBackup = {
			...masterBackup,
			ids: finalIds,
		};

		await syncBackupToStorage(updatedBackup);

		onProgress?.(
			`Discovered ${newProfiles.length} new on-chain identities`,
			"completed"
		);
	} else {
		onProgress?.("No new on-chain identities found", "completed");
	}
	return newProfiles.length;
}

/**
 * Import a discovered identity into the backup
 * This adds the identity to the backup's ids so it persists
 */
export function importDiscoveredIdentity(
	counter: number,
	name?: string
): {
	identity: ResolvedIdentity;
	updatedBackup: BapMasterBackup;
} | null {
	const backup = getDecryptedBackup();
	if (!backup) {
		return null;
	}

	// Use centralized BAP instance getter
	const bap = getBAPInstance();
	if (!bap) {
		return null;
	}

	const bapIdentity = bap.newId();
	const bapId = bapIdentity.bapId;

	// Export updated ids
	const updatedIds = bap.exportIds();

	// Create updated backup
	const updatedBackup = {
		...backup,
		ids: updatedIds,
	} as BapMasterBackup;

	const identity: ResolvedIdentity = {
		bapId,
		name: name || `Identity ${counter}`,
		rootAddress: bapIdentity.rootAddress,
		counter,
	};

	return { identity, updatedBackup };
}

/**
 * Get BAP instance initialized from backup
 * Returns null if no backup or if it's a member/WIF backup (which can't create new identities)
 */
export function getBAPInstance(): BAP | null {
	const backup = getDecryptedBackup();
	if (!backup) {
		return null;
	}

	if (isSeedIdentityBackup(backup)) {
		return null;
	}
	let bap: BAP;

	if (isLegacyBackup(backup)) {
		bap = new BAP(backup.xprv);
	} else if (isType42Backup(backup)) {
		bap = new BAP({ rootPk: backup.rootPk });
	} else {
		return null;
	}

	// Import existing identities. A failed import must throw — swallowing it
	// here returned a BAP with zero ids and caused downstream "key doesn't
	// match id" confusion (OPL-2434). Legacy (pre-0.3) backups are upgraded
	// once at sign-in via reissueLegacyBackupIfNeeded, so by the time this
	// runs the stored backup is current-format.
	if (backup.ids && backup.ids.length > 0) {
		bap.importIds(backup.ids);
	}

	return bap;
}

/** Selected seed authority has no ancestor or sibling derivation capability. */
export function getSeedAdapter(): SeedProfileAdapter | null {
	const backup = getDecryptedBackup();
	return isSeedIdentityBackup(backup) ? new SeedProfileAdapter(backup) : null;
}
export function getAccountIdentity(
	bapId: string
): MasterID | SeedProfileIdentity | null {
	const seed = getSeedAdapter();
	return seed ? seed.getId(bapId) : (getBAPInstance()?.getId(bapId) ?? null);
}
/** Commit both copies before exposing the newly allocated peer for registration. */
export async function createSeedIdentity(
	name: string,
	password: string
): Promise<{ bapId: string; accountPubkey: string; counter: number }> {
	const adapter = getSeedAdapter();
	if (!adapter) {
		throw new Error("A seed backup is required");
	}
	const encrypted = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (!encrypted) {
		throw new Error("A portable seed backup is required");
	}
	const verified = await decryptIdentityBackup(encrypted, password);
	if (JSON.stringify(verified) !== JSON.stringify(adapter.exportBackup())) {
		throw new Error("Unlocked and portable backups do not match");
	}
	if (
		localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !== encrypted ||
		JSON.stringify(getDecryptedBackup()) !==
			JSON.stringify(adapter.exportBackup())
	) {
		throw new Error("Backup changed while authorizing profile creation");
	}
	const identity = adapter.newId();
	const updated = adapter.exportBackup();
	const profile = updated.profiles.find(
		(entry) => entry.bapId === identity.bapId
	);
	if (!profile) {
		throw new Error("Invalid seed profile inventory");
	}
	profile.metadata = { ...profile.metadata, name };
	const result = await syncBackupToStorage(updated, password, {
		requirePortable: true,
		deferCloudFlush: true,
	});
	if (!(result.success && result.portablePersisted)) {
		throw new Error(result.error ?? "Failed to persist seed profile");
	}
	return {
		bapId: identity.bapId,
		accountPubkey: getAccountPubkey(identity),
		counter: identity.index,
	};
}

/**
 * Create a new BAP identity from master key.
 * Returns the new identity's bapId + account (member) pubkey, or null if it
 * can't create one (member/WIF backup).
 */
export function createNewIdentity(
	_name: string
): { bapId: string; accountPubkey: string } | null {
	const bap = getBAPInstance();
	if (!bap) {
		return null;
	}

	const newIdentity = bap.newId();
	const bapId = newIdentity.bapId;
	// Per-identity account (member) pubkey; the caller registers it as
	// profile.account_pubkey.
	const accountPubkey = getAccountPubkey(newIdentity);

	// Persist the new id into the session backup so the caller sees it before
	// re-encrypting the master backup.
	const backup = getDecryptedBackup();
	if (backup) {
		const updatedIds = bap.exportIds();
		updateBapIdsInBackup(updatedIds).catch(() => undefined);
	}

	return { bapId, accountPubkey };
}

/**
 * Fetch BAP profile from blockchain via Sigma API
 */
const FETCH_TIMEOUT_MS = 3000; // 3 second timeout

/**
 * Expand relative media references and normalize image URLs in a profile.
 */
function normalizeProfileMedia(profile: BAPProfile): void {
	if (!profile.identity) {
		return;
	}

	// Determine attestation txid for expanding relative refs like "_0"
	const attestationTxid =
		(profile.txHash && profile.txHash !== "_" ? profile.txHash : undefined) ??
		profile.addresses?.[profile.addresses.length - 1]?.txId ??
		undefined;

	const expandRef = (value: string | undefined): string | undefined => {
		if (!(value && attestationTxid)) {
			return value;
		}
		const trimmed = value.trim();
		if (!trimmed.startsWith("_")) {
			return value;
		}
		const vout = Number.parseInt(trimmed.slice(1), 10);
		if (!Number.isFinite(vout) || vout < 0) {
			return value;
		}
		return `/${attestationTxid}_${vout}`;
	};

	profile.identity.image = expandRef(profile.identity.image);
	profile.identity.logo = expandRef(profile.identity.logo);
	profile.identity.banner = expandRef(profile.identity.banner);

	// Normalize inscription URLs to the 1sat content gateway
	if (profile.identity.image) {
		const normalized = normalizeImageUrl(profile.identity.image);
		if (normalized) {
			profile.identity.image = normalized;
		}
	}
	if (profile.identity.banner) {
		const normalized = normalizeImageUrl(profile.identity.banner);
		if (normalized) {
			profile.identity.banner = normalized;
		}
	}
}

export async function fetchBAPProfile(
	bapId: string
): Promise<BAPProfile | null> {
	try {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

		const response = await fetch(
			`/api/bap/profile?bapId=${encodeURIComponent(bapId)}`,
			{
				method: "GET",
				signal: controller.signal,
			}
		);

		clearTimeout(timeoutId);

		if (response.status === 404) {
			return null;
		}

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data: BAPResponse = await response.json();

		if (data.status !== "OK" || !data.result) {
			return null;
		}

		normalizeProfileMedia(data.result);

		if (data.isDraft) {
			data.result.isDraft = true;
		}

		return data.result;
	} catch {
		return null;
	}
}

/** Thrown by {@link fetchBAPProfileForQuery} when the lookup did not complete. */
export class BAPProfileFetchError extends Error {
	readonly bapId: string;
	readonly status: number | null;

	constructor(bapId: string, status: number | null, cause?: unknown) {
		super(
			status === null
				? `BAP profile lookup for ${bapId} did not complete`
				: `BAP profile lookup for ${bapId} failed with HTTP ${status}`,
			{ cause }
		);
		this.name = "BAPProfileFetchError";
		this.bapId = bapId;
		this.status = status;
	}
}

const QUERY_FETCH_TIMEOUT_MS = 10_000;

/**
 * Profile fetcher for TanStack Query.
 *
 * Unlike {@link fetchBAPProfile}, only a 404 means "no profile". Timeouts,
 * aborted requests (a navigation mid-fetch during the sign-in redirects),
 * 5xx and malformed responses THROW, so the query layer retries them and
 * never caches a transport failure as "this identity has no name". That
 * caching is what showed "Identity 1" and a placeholder avatar for a
 * published identity until the page was refreshed.
 */
export async function fetchBAPProfileForQuery(
	bapId: string
): Promise<BAPProfile | null> {
	const controller = new AbortController();
	const timeoutId = setTimeout(
		() => controller.abort(),
		QUERY_FETCH_TIMEOUT_MS
	);

	let response: Response;
	try {
		response = await fetch(
			`/api/bap/profile?bapId=${encodeURIComponent(bapId)}`,
			{ method: "GET", signal: controller.signal }
		);
	} catch (error) {
		throw new BAPProfileFetchError(bapId, null, error);
	} finally {
		clearTimeout(timeoutId);
	}

	if (response.status === 404) {
		return null;
	}
	if (!response.ok) {
		throw new BAPProfileFetchError(bapId, response.status);
	}

	let data: BAPResponse;
	try {
		data = await response.json();
	} catch (error) {
		throw new BAPProfileFetchError(bapId, response.status, error);
	}
	if (data.status !== "OK" || !data.result) {
		throw new BAPProfileFetchError(bapId, response.status);
	}

	normalizeProfileMedia(data.result);
	if (data.isDraft) {
		data.result.isDraft = true;
	}
	return data.result;
}
